import { Controller } from 'stimulus';
import { announce } from '../javascript/lib/a11y/announcement';
import { t } from '../javascript/lib/i18n';

export default class extends Controller {
  static targets = ['source', 'button'];

  originalButtonInnerHTML = null;
  copiedTimeoutId = null;

  async copy() {
    // If the button is already disabled, ignore subsequent clicks so the
    // "Copied!" state never gets stuck due to overlapping timeouts.
    if (this.hasButtonTarget && this.buttonTarget.disabled) return;

    this.lockButton();

    const html = this.sourceTarget.innerHTML;
    const plain = this.sourceTarget.textContent;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);

      this.showCopied();
      announce(t('js.common.copied'));
    } catch {
      // Fallback: copy as plain text
      try {
        await navigator.clipboard.writeText(plain);
        this.showCopied();
        announce(t('js.copy_html.copied_as_plain_text'));
      } catch {
        // Ensure we don't leave the UI in a disabled state if all clipboard
        // attempts fail.
        this.unlockButton();
        announce(t('js.copy_html.copy_failed'));
      }
    }
  }

  lockButton() {
    if (!this.hasButtonTarget) return;

    if (this.originalButtonInnerHTML === null) {
      this.originalButtonInnerHTML = this.buttonTarget.innerHTML;
    }

    this.buttonTarget.disabled = true;
    this.buttonTarget.setAttribute('aria-disabled', 'true');
  }

  unlockButton() {
    if (!this.hasButtonTarget) return;

    this.buttonTarget.disabled = false;
    this.buttonTarget.removeAttribute('aria-disabled');
  }

  showCopied() {
    if (!this.hasButtonTarget) return;

    // If we somehow entered "showCopied" multiple times, don't schedule
    // multiple restores that could fight each other.
    if (this.copiedTimeoutId !== null) return;

    this.buttonTarget.textContent = t('js.common.copied');

    this.copiedTimeoutId = setTimeout(() => {
      this.buttonTarget.innerHTML = this.originalButtonInnerHTML;
      this.originalButtonInnerHTML = null;
      this.copiedTimeoutId = null;
      this.unlockButton();
    }, 2000);
  }
}
